import React from 'react';
import { Grid } from 'semantic-ui-react';

import {
  FlexibleWidthXYPlot,
  HorizontalGridLines,
  VerticalGridLines,
  XAxis,
  YAxis,
  DiscreteColorLegend,
  LineMarkSeries,
  VerticalBarSeries,
} from 'react-vis';

import { ClassProp } from 'helpers/types';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import { getTickFormat, Timeslice } from 'scenes/Report/Report.helper';
import WithHint, { HintArgs } from 'scenes/Report/components/WithHint';
import { theme } from 'config';

import ReplaceTextConfig from './Transactions.batch.config';
import styles from './Transactions.styles';
import { scalingData, tickScalingData } from '../PerformanceReports.helper';
import {
  transactionTransformers,
  salesTransformers,
  legendItems,
  getStartDate,
  getEndDate,
} from './Transactions.helpers';

type Styles = ClassProp<typeof styles>;

export const tickFormat = (timeslice: Timeslice) => (
  value: string,
): JSX.Element => {
  const { top, bottom } = getTickFormat(timeslice, value);

  return (
    <tspan>
      <tspan x="0" dy="1em">
        {top}
      </tspan>

      <br />

      <tspan x="0" dy="1em">
        {bottom}
      </tspan>
    </tspan>
  );
};

export const renderCharts = (
  params: ReportParams,
  header: string,
  batchKey: string,
  segmentValue: number | string | null,
  title: string,
  classes: Styles,
  height: number,
  idx: number,
  transactions?: any[] | null,
): JSX.Element => {
  const { timeslice } = params;
  const transactionData = transactionTransformers(transactions);
  const salesData = salesTransformers(transactions);
  return (
    <ReportBase
      batchKey={batchKey}
      header={header}
      title={title}
      params={params}
      segmentedBatchData={{
        idx,
        startDate: getStartDate(transactionData),
        endDate: getEndDate(transactionData),
        replaceTextConfig: ReplaceTextConfig,
        isSegmented: true,
        segmentValue,
      }}
      key={idx}
    >
      <WithHint data={{ transactionData, salesData }}>
        {({ onValueMouseOver, onValueMouseOut, renderHint }: HintArgs) => (
          <Grid.Row centered className={classes.charts}>
            <Grid.Column textAlign="center" width={14}>
              <FlexibleWidthXYPlot xType="ordinal" height={height}>
                <VerticalGridLines />
                <HorizontalGridLines />
                <XAxis tickFormat={tickFormat(timeslice as Timeslice)} />

                <YAxis
                  orientation="right"
                  tickFormat={tickScalingData(transactionData, ',.0f')}
                  title="Number of Transactions"
                />

                <YAxis
                  orientation="left"
                  tickFormat={tickScalingData(salesData)}
                  title="Revenue"
                />

                <VerticalBarSeries
                  color={theme.violet}
                  data={scalingData(salesData)}
                />

                <LineMarkSeries
                  onValueMouseOver={onValueMouseOver('transactionData', ',.0f')}
                  onValueMouseOut={onValueMouseOut}
                  color={theme.seafoamGreen}
                  data={scalingData(transactionData)}
                />

                {renderHint()}
              </FlexibleWidthXYPlot>
            </Grid.Column>

            <Grid.Row centered>
              <DiscreteColorLegend
                className={classes.legend}
                orientation="horizontal"
                items={legendItems}
              />
            </Grid.Row>
          </Grid.Row>
        )}
      </WithHint>
    </ReportBase>
  );
};
